/**
 * Cómo está un equipo de Clobi Conector según su último latido (F4.4a de PLAN_AGENTE_LOCAL).
 * Los umbrales del reloj son los de `apps/pos/latido.py`.
 */
import type { PosTerminal, TerminalHeartbeat } from './hooks/usePos'

/** Late cada minuto: tres latidos perdidos ya es «sin conexión». */
export const MINUTOS_EN_LINEA = 3
const AVISO_RELOJ_SEGUNDOS = 2 * 60
const BLOQUEO_RELOJ_SEGUNDOS = 15 * 60

export type Tono = 'success' | 'warning' | 'danger' | 'muted'

export interface Indicador {
  texto: string
  tono: Tono
}

export interface EstadoDelEquipo {
  /** `null`: el equipo nunca latió (un POS, o un conector anterior al latido). */
  conexion: Indicador | null
  /** Lo que conviene mirar: cola, errores, reloj. Vacío si todo está bien. */
  avisos: Indicador[]
  /** El detalle para el `title`: equipo, sistema, impresoras y último error. */
  detalle: string
}

function minutosTexto(segundos: number): string {
  const min = Math.round(Math.abs(segundos) / 60)
  const signo = segundos > 0 ? '+' : '−'
  return min >= 60 ? `${signo}${Math.round(min / 60)} h` : `${signo}${min} min`
}

function latido(t: PosTerminal): TerminalHeartbeat | null {
  return t.heartbeat_at && 'queue' in t.heartbeat ? (t.heartbeat as TerminalHeartbeat) : null
}

export function estadoDelEquipo(t: PosTerminal, ahora: number = Date.now()): EstadoDelEquipo {
  const h = latido(t)
  if (!h || !t.heartbeat_at) return { conexion: null, avisos: [], detalle: '' }

  const minutos = (ahora - new Date(t.heartbeat_at).getTime()) / 60000
  const conexion: Indicador = minutos <= MINUTOS_EN_LINEA
    ? { texto: 'En línea', tono: 'success' }
    : { texto: 'Sin conexión', tono: 'warning' }

  const avisos: Indicador[] = []
  const skew = h.clock_skew_seconds
  if (skew != null && Math.abs(skew) > BLOQUEO_RELOJ_SEGUNDOS) {
    avisos.push({ texto: `Reloj ${minutosTexto(skew)}: no emite`, tono: 'danger' })
  } else if (skew != null && Math.abs(skew) > AVISO_RELOJ_SEGUNDOS) {
    avisos.push({ texto: `Reloj ${minutosTexto(skew)}`, tono: 'warning' })
  }
  if (h.queue.failed) avisos.push({ texto: `${h.queue.failed} para revisar`, tono: 'danger' })
  if (h.queue.pending) avisos.push({ texto: `${h.queue.pending} por subir`, tono: 'warning' })

  const lineas = [
    h.host.machine && `Equipo: ${h.host.machine}`,
    h.host.os && `Sistema: ${h.host.os}`,
    h.app_version && `Versión: ${h.app_version}`,
    h.printers.length > 0 && `Impresoras: ${h.printers.join(', ')}`,
    h.last_error?.message && `Último error: ${h.last_error.message}`,
  ].filter(Boolean)
  return { conexion, avisos, detalle: lineas.join('\n') }
}
